import { useContext } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import ImageUpload from "components/ImageUpload";
import FormWrapper from "components/FormWrapper";
import ResetAndSubmit from "components/ResetAndSubmit";
import * as Yup from "yup";
import TextArea from "components/TextArea";
import { AppContext } from "state";
import { getImageValidationSchema } from "constants/validationSchemas";
import Card from "components/OnboardingFormCard";
import { Img } from "components";
import BrandingActionCard from "components/NewCards/BrandingActionCard";
import GlobalDrawer from "components/Drawer";
import _ from "lodash";

// Helper function to check if any material has data
const hasAnyMaterialData = (materials) => {
    return materials.some(material => 
        material.MATERIAL_NAME || 
        material.MATERIAL_TYPE || 
        material.MATERIAL_IMAGE ||
        material.MATERIAL_DESCRIPTION
    );
};

// Dynamic validation schema based on whether user has filled any data
const getValidationSchema = (materials) => {
    const hasData = hasAnyMaterialData(materials);
    
    return Yup.object({
        materials: Yup.array().of(
            Yup.object({
                MATERIAL_IMAGE: Yup.string().when("hasMaterials", (hasMaterials, schema) => {
                    if (!hasData) return schema; // No validation if no data filled
                    return hasMaterials?.[0] ? getImageValidationSchema("Image is required") : schema;
                }),
                MATERIAL_NAME: Yup.string().when("hasMaterials", (hasMaterials, schema) => {
                    if (!hasData) return schema; // No validation if no data filled
                    return hasMaterials?.[0] ? schema.required("Name is required") : schema;
                }),
                MATERIAL_TYPE: Yup.string().when("hasMaterials", (hasMaterials, schema) => {
                    if (!hasData) return schema; // No validation if no data filled
                    return hasMaterials?.[0] ? schema.required("Material Type is required") : schema;
                }),
            })
        ),
    });
};

const NewBranding = ({ handleNavigate, resetFormData, disableForm }: { handleNavigate, resetFormData?: any, titleAndDesNotRequired?: boolean, disableForm?: boolean }) => {
    const { globalData } = useContext(AppContext);
    let instituteData = null;
    try {
        instituteData = JSON.parse(sessionStorage?.getItem('_DATA_') || '{}');
    } catch (err) { }
    
    const emptyMaterialData = {
        hasMaterials: true,
        DRAWER: false,
        MATERIAL_IMAGE: "",
        MATERIAL_NAME: "",
        MATERIAL_TYPE: "",
        MATERIAL_DESCRIPTION: "",
    };

    const loadInitialData = () => {
        const savedData = JSON.parse(sessionStorage.getItem("materials")) || [];
        return savedData.length ? savedData : [emptyMaterialData];
    };

    return <Formik
        initialValues={{ materials: loadInitialData() }}
        enableReinitialize
        validate={(values) => {
            // Use dynamic validation schema
            const schema = getValidationSchema(values.materials);
            try {
                schema.validateSync(values, { abortEarly: false });
                return {};
            } catch (error) {
                const errors = {};
                error.inner.forEach(err => {
                    if (err.path) {
                        const keys = err.path.split('.');
                        let current = errors;
                        for (let i = 0; i < keys.length - 1; i++) {
                            if (!current[keys[i]]) {
                                current[keys[i]] = isNaN(keys[i + 1]) ? {} : [];
                            }
                            current = current[keys[i]];
                        }
                        current[keys[keys.length - 1]] = err.message;
                    }
                });
                return errors;
            }
        }}
        onSubmit={(values) => {
            if(!!values.materials?.[0]?.MATERIAL_NAME || !values.materials?.[0]?.hasMaterials) {
                sessionStorage.setItem("materials", JSON.stringify(values.materials));
            }
            handleNavigate(true);
        }}
    >
        {({ values, handleSubmit, resetForm, errors, setFieldValue }) => {
            const hasData = hasAnyMaterialData(values.materials);
            const hasValidationErrors =  !_.isEmpty(errors);
            
            // Check if form should be treated as incomplete
            const isIncomplete = hasData && hasValidationErrors;

            return <FormWrapper
                title={"Brand Marketing Materials"}
                description={"Brochures, Flyers & Posters That Leave a Lasting Impression"}
                childern={<Form onSubmit={handleSubmit}>
                    <div>
                        <div className="rounded-2xl my-1 mx-1">
                            <FieldArray name="materials">
                                {({ push, remove, form }) => {
                                    const element = form?.values?.materials?.[0];
                                    const handleOptionChange = e => {
                                        resetForm();
                                        form.setFieldValue(`materials.${0}.hasMaterials`, e);
                                    }
                                    const addMore = () => !!values.materials?.[0]?.MATERIAL_NAME && values.materials.length < 5 ? setFieldValue("materials", [...(values.materials || []), { ...emptyMaterialData, DRAWER: true }]) : "";
                                    
                                    return <div className="flex flex-col space-y-4 sm:space-y-4 md:space-y-4 lg:space-y-4 mb-2">
                                        <div className="my-4">
                                            <div className="grid grid-cols-12 gap-4 w-full p-4 bg-[#F5F5F5] rounded-2xl">
                                                <div className="col-span-4 bg-white rounded-lg shadow-lg p-6 flex flex-col items-center justify-center w-[90%] max-w-md gap-4">
                                                    {/* Brand Icon */}
                                                    <div className="mb-2">
                                                        <div className="relative">
                                                            <Img src="/images/Branding.svg" alt="brand frame" className="w-[14vh] h-[14vh]" />
                                                        </div>
                                                    </div>

                                                    {/* Question Text */}
                                                    <div className="text-[2vh] text-center font-manropeB">Have Materials Already?</div>

                                                    {/* Radio Options */}
                                                    <div className="flex flex-col items-center justify-center w-full gap-2">
                                                        <label className="flex items-center cursor-pointer">
                                                            <div className={`w-[2vh] h-[2vh] rounded-full border flex items-center justify-center ${element?.hasMaterials ? 'border-indigo-600' : 'border-gray-300'}`}>
                                                                {element?.hasMaterials && (
                                                                    <div className="w-[1vh] h-[1vh] bg-indigo-600 rounded-full"></div>
                                                                )}
                                                            </div>
                                                            <input
                                                                type="radio"
                                                                className="hidden"
                                                                checked={element?.hasMaterials}
                                                                onChange={() => handleOptionChange(true)}
                                                            />
                                                            <span className="ml-2 text-[2vh]">Yes, we do</span>
                                                        </label>

                                                        <label className="flex items-center cursor-pointer">
                                                            <div className={`w-[2vh] h-[2vh] rounded-full border flex items-center justify-center ${!element?.hasMaterials ? 'border-indigo-600' : 'border-gray-300'}`}>
                                                                {!element?.hasMaterials && (
                                                                    <div className="w-[1vh] h-[1vh] bg-indigo-600 rounded-full"></div>
                                                                )}
                                                            </div>
                                                            <input
                                                                type="radio"
                                                                className="hidden"
                                                                checked={!element?.hasMaterials}
                                                                onChange={() => handleOptionChange(false)}
                                                            />
                                                            <span className="ml-2 text-[2vh]">No, design my material</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                {!element?.hasMaterials ? <div className="col-span-8 bg-[#1ba85644] rounded-2xl border border-[#1BA856]">
                                                    <div className="grid grid-cols-12 gap-4 p-4">
                                                        <div className="text-[white] bg-[#1BA856] rounded-xl col-span-4 flex flex-col justify-between gap-6 w-full p-4">
                                                            <button type='button' className="text-[#F5F5F5] font-manrope rounded-full bg-[#ffffff34] py-2 px-4 w-fit">
                                                                One time purchase
                                                            </button>
                                                            <p className="text-[white] text-[9vh] font-manropeEB">₹5,000</p>
                                                            <p className="text-[white] text-[3vh] font-manropeM">Per Brand Material</p>
                                                        </div>
                                                        <div className="col-span-8">
                                                            <div className="flex flex-col justify-between h-full">
                                                                <div className="space-y-[2.5vh]">
                                                                    <div className="flex items-center gap-2">
                                                                        <Img src={"images/Onboarding/Check.svg"} />
                                                                        <p className="font-manropeM text-[2vh]">Custom poster design, eye catching & onbrand</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Img src={"images/Onboarding/Check.svg"} />
                                                                        <p className="font-manropeM text-[2vh]">Professional brochure, perfect for admissions & events</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Img src={"images/Onboarding/Check.svg"} />
                                                                        <p className="font-manropeM text-[2vh]">Engaging flyer design, ideal for promotions & campaigns</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Img src={"images/Onboarding/Check.svg"} />
                                                                        <p className="font-manropeM text-[2vh]">Unlimited revisions, until you're 100% satisfied</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Img src={"images/Onboarding/Check.svg"} />
                                                                        <p className="font-manropeM text-[2vh]">High resolution files print ready & digital formats</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        style={{ color: '#fff' }}
                                                                        className={`w-fit px-4 py-2 text-[2.4vh] font-medium text-white bg-purple-primary rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none`}
                                                                    >
                                                                        Request for purchase
                                                                    </button>
                                                                    <div className="w-[2px] h-[5vh] bg-gray-500"></div>
                                                                    <div>
                                                                        <p className="text-gray-500 font-manropeSB text-[1.8vh]">
                                                                            Our team will contact you shortly to discuss next <br /> steps and your brand material requirements.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div> : <div className="col-span-8 space-y-2">
                                                    <div className="col-span-12 flex justify-between items-center">
                                                        <h1 className="text-[3vh] font-manropeB">Add Brand Marketing Material</h1>
                                                        {!!element?.SAVED && <button
                                                            onClick={addMore}
                                                            type="button"
                                                            style={{ color: '#fff' }}
                                                            className={`inline-flex items-center px-4 py-2 text-[1.8vh] font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none`}
                                                        >
                                                            Upload Material
                                                        </button>}
                                                    </div>
                                                    <div className="col-span-12 mt-2">
                                                        {values.materials.map((material, index) => {

                                                            const handleCloseDrawer = () => form.setFieldValue(`materials.${index}.DRAWER`, false);

                                                            const onDelete = () => {
                                                                if (values.materials?.length > 1) {
                                                                    remove(index)
                                                                } else {
                                                                    remove(index)
                                                                    push(emptyMaterialData)
                                                                }
                                                            }

                                                            return <>
                                                                {!!element?.SAVED ? <BrandingActionCard
                                                                    isError={Boolean(errors?.materials?.[index])}
                                                                    element={material}
                                                                    handleOpenDrawer={() => form.setFieldValue(`materials.${index}.DRAWER`, true)}
                                                                    onDelete={onDelete}
                                                                /> : <div className="grid grid-cols-12 gap-x-2 gap-y-4">
                                                                    <div className="col-span-6">
                                                                        <Input
                                                                            disable={disableForm}
                                                                            label="Material Name"
                                                                            name={`materials.${index}.MATERIAL_NAME`}
                                                                            type="text"
                                                                            placeHolder="Enter material name"
                                                                            required={hasData} // Only required if user has filled any data
                                                                        />
                                                                    </div>
                                                                    <div className="col-span-6">
                                                                        <Input
                                                                            disable={disableForm}
                                                                            label="Material type"
                                                                            name={`materials.${index}.MATERIAL_TYPE`}
                                                                            type="text"
                                                                            placeHolder="Enter Material type "
                                                                            required={hasData} // Only required if user has filled any data
                                                                        />
                                                                    </div>
                                                                    <div className="col-span-12">
                                                                        <Field name={`materials.${index}.MATERIAL_IMAGE`}>
                                                                            {({ field, form }) => (
                                                                                <ImageUpload
                                                                                    customMessage={"JPG, JPEG or PDF (50 MB)"}
                                                                                    allow="application/pdf"
                                                                                    disable={disableForm}
                                                                                    label={<label>Upload {!!hasData && <span className="text-[red]">*</span>}</label>}
                                                                                    maxSize={5}
                                                                                    labelClassNames="text-[2vh]"
                                                                                    containerHeight={{
                                                                                        width: "7vh",
                                                                                        height: "7vh",
                                                                                    }}
                                                                                    imageValue={field.value}
                                                                                    onChange={(item) => form.setFieldValue(`materials.${index}.MATERIAL_IMAGE`, item)}
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                    </div>
                                                                    <div className="col-span-12">
                                                                        <button
                                                                            disabled={Boolean(errors?.materials?.[index])}
                                                                            onClick={() => form.setFieldValue(`materials.${index}.SAVED`, true)}
                                                                            type="button"
                                                                            style={{ color: '#fff' }}
                                                                            className={`${Boolean(errors?.materials?.[index]) ? "opacity-25 " : " "} inline-flex items-center px-4 py-2 text-[1.8vh] font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none`}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </div>
                                                                </div>}
                                                                <GlobalDrawer
                                                                    open={material?.DRAWER}
                                                                    onClose={handleCloseDrawer}
                                                                    drawerHeading={<span className="text-[2vh]">{`Add Brand Marketing Material`}</span>}
                                                                    width={650}
                                                                    placement="right"
                                                                >
                                                                    <div className="flex flex-wrap gap-x-2 gap-y-4">
                                                                        <div className="w-full">
                                                                            <Field name={`materials.${index}.MATERIAL_IMAGE`}>
                                                                                {({ field, form }) => (
                                                                                    <ImageUpload
                                                                                        customMessage={"JPG, JPEG or PDF (50 MB)"}
                                                                                        allow="application/pdf"
                                                                                        disable={disableForm}
                                                                                        label="Upload"
                                                                                        maxSize={5}
                                                                                        labelClassNames="text-[2vh]"
                                                                                        containerHeight={{
                                                                                            width: "7vh",
                                                                                            height: "7vh",
                                                                                        }}
                                                                                        imageValue={field.value}
                                                                                        onChange={(item) => form.setFieldValue(`materials.${index}.MATERIAL_IMAGE`, item)}
                                                                                    />
                                                                                )}
                                                                            </Field>
                                                                        </div>
                                                                        <div className="w-full">
                                                                            <Input
                                                                                disable={disableForm}
                                                                                label="Material Name"
                                                                                name={`materials.${index}.MATERIAL_NAME`}
                                                                                type="text"
                                                                                placeHolder="Enter material name"
                                                                                required={hasData} // Only required if user has filled any data
                                                                            />
                                                                        </div>
                                                                        <div className="w-full">
                                                                            <Input
                                                                                disable={disableForm}
                                                                                label="Material type"
                                                                                name={`materials.${index}.MATERIAL_TYPE`}
                                                                                type="text"
                                                                                placeHolder="Enter Material type "
                                                                                required={hasData} // Only required if user has filled any data
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex justify-end items-center gap-4 mt-4">
                                                                        <button
                                                                            type="button"
                                                                            className="px-4 py-2 text-[1.8vh] font-medium text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100"
                                                                            onClick={onDelete}
                                                                        >
                                                                            Reset
                                                                        </button>
                                                                        <button
                                                                            disabled={Boolean(errors?.materials?.[index])}
                                                                            onClick={handleCloseDrawer}
                                                                            type="button"
                                                                            style={{ color: '#fff' }}
                                                                            className={`${Boolean(errors?.materials?.[index]) ? "opacity-25 " : " "} inline-flex items-center px-4 py-2 text-[1.8vh] font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none`}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </div>
                                                                </GlobalDrawer>
                                                            </>
                                                        })}
                                                    </div>
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                }}
                            </FieldArray>
                        </div>
                    </div>
                    {(!disableForm || !instituteData?.BRANCH_ID) && <ResetAndSubmit
                        submitLabel="Submit"
                        submitToolTip={
                            values?.materials?.[0]?.hasMaterials && isIncomplete
                                ? (!Boolean(values?.materials?.filter(item => !!item?.MATERIAL_NAME)?.length) 
                                    ? "Please add at least one material" 
                                    : "Please fill all the required fields")
                                : ""
                        }
                        isError={values?.materials?.[0]?.hasMaterials && isIncomplete}
                        resetForm={() => {
                            resetForm();
                            resetFormData();
                        }}
                    />}
                </Form>}
            />
        }}
    </Formik>
}

export default NewBranding;